import Cookies from 'js-cookie';

export interface CookieConsentState {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  timestamp: string;
  version: string;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    _fbq?: (...args: unknown[]) => void;
  }
}

const CONSENT_VERSION = '1.0';
const CONSENT_STORAGE_KEY = 'tv_cookie_consent_v1';

export function getStoredConsent(): CookieConsentState | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CookieConsentState;
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;

    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      preferences: Boolean(parsed.preferences),
      timestamp: parsed.timestamp,
      version: parsed.version,
    };
  } catch {
    return null;
  }
}

export function saveConsent(partial: {
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}): CookieConsentState {
  const consent: CookieConsentState = {
    necessary: true,
    analytics: partial.analytics,
    marketing: partial.marketing,
    preferences: partial.preferences,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };

  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
  return consent;
}

function ensureGtagStub() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      (window.dataLayer as unknown[]).push(args);
    };
  }
}

export function applyGoogleConsentDefault() {
  ensureGtagStub();
  window.gtag?.('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  });
}

export function applyGoogleConsent(consent: CookieConsentState) {
  ensureGtagStub();

  window.gtag?.('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    functionality_storage: consent.preferences ? 'granted' : 'denied',
    personalization_storage: consent.preferences ? 'granted' : 'denied',
    security_storage: 'granted',
  });
}

export async function logConsentToServer(consent: CookieConsentState): Promise<void> {
  try {
    const token = Cookies.get('authToken');
    await fetch('/api/consent/log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        ...consent,
        source: 'banner',
        path: window.location.pathname,
      }),
    });
  } catch (error) {
    console.warn('Failed to persist cookie consent decision:', error);
  }
}
